'use client';

import { useEffect, useRef, type ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import { hasSiteScenery } from '@/lib/site-scenery';

/** Keep the accepted cursor effect outside public hero artwork. Its simulation,
 * welcome appearance and input handling remain owned by SplashCursor. */
export function PublicHeroSmokeBoundary({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  useEffect(() => {
    const surface = ref.current;
    if (!surface || !hasSiteScenery(pathname)) return;
    let frame = 0;
    let hero: Element | null = null;
    const paint = () => {
      frame = 0;
      if (!hero?.isConnected) { surface.style.clipPath = ''; return; }
      const rect = hero.getBoundingClientRect();
      const width = innerWidth, height = innerHeight;
      const left = Math.max(0, rect.left), right = Math.min(width, rect.right);
      const top = Math.max(0, rect.top), bottom = Math.min(height, rect.bottom);
      // Opposite winding cuts an exact rectangular hole from the viewport.
      // Clip the effect, not the image: transparent uploads retain their backing.
      surface.style.clipPath = bottom > top && right > left
        ? `path("M0 0H${width}V${height}H0Z M${left} ${top}V${bottom}H${right}V${top}Z")`
        : '';
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(paint); };
    const resize = new ResizeObserver(schedule);
    const findHero = () => {
      const next = document.querySelector('.public-site [data-scenery-hero]');
      if (next === hero) return;
      if (hero) resize.unobserve(hero);
      hero = next;
      if (hero) resize.observe(hero);
      schedule();
    };
    const mutations = new MutationObserver(findHero);
    mutations.observe(document.body, { childList: true, subtree: true });
    const header = document.querySelector('.public-site header');
    if (header) resize.observe(header);
    findHero(); cancelAnimationFrame(frame); paint();
    window.addEventListener('scroll', schedule, { passive: true, capture: true });
    window.addEventListener('resize', schedule, { passive: true });
    return () => {
      cancelAnimationFrame(frame); resize.disconnect(); mutations.disconnect();
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
      surface.style.clipPath = '';
    };
  }, [pathname]);
  return <div ref={ref} data-public-smoke-boundary className="pointer-events-none fixed inset-0">{children}</div>;
}
